import { z } from 'zod';
/**
 * `data-consumer-quickstart.md` listed a tight 6-value enum, but the live
 * column actually carries `unspecified | repair | alteration | new_build |
 * addition | demolition | certificate | tenant_improvement | change_of_use`
 * plus NULL. Accept any non-empty string so a future taxonomy change does
 * not reject every row.
 */
export const PermitScope = z.string();
/**
 * Source-published statuses are normalized into a small set, but the doc
 * warns that some sources publish weird values. Accept any non-empty string
 * rather than reject the row.
 */
export const PermitStatus = z.string();
/**
 * BigQuery's Node SDK returns DATE columns as `{ value: 'YYYY-MM-DD' }`.
 * Normalize to the ISO string so consumers don't care which form they got.
 */
const BqDate = z
    .union([z.string(), z.object({ value: z.string() })])
    .transform((v) => (typeof v === 'string' ? v : v.value))
    .nullable();
const NullableString = z.string().nullable();
const NullableNumber = z.number().nullable();
const StringArray = z.array(z.string());
export const PermitRow = z.object({
    permit_id: z.string(),
    source_id: z.string(),
    source_permit_number: NullableString,
    address_line1: NullableString,
    address_line2: NullableString,
    city: NullableString,
    state: NullableString,
    postal_code: NullableString,
    latitude: NullableNumber,
    longitude: NullableNumber,
    address_geocodeable: z.boolean().nullable(),
    issued_date: BqDate,
    /**
     * How precise `issued_date` is (molluskio#5313): 'day' | 'month' | 'year', or
     * NULL (unknown, i.e. treated as a day). A month or year date is stored as
     * `YYYY-MM-01` / `YYYY-01-01`. Display it with `formatIssuedDate`.
     * Any string is accepted, so a new value cannot reject the row.
     * `.default(null)`: the API selects the column only once the served view
     * carries it (the first materialize after molluskio#5319).
     */
    issued_date_precision: NullableString.default(null),
    applied_date: BqDate,
    final_date: BqDate,
    expiration_date: BqDate,
    /**
     * Canonical permit date = COALESCE(issued, applied, final), materialized
     * upstream (molluskio#1372) so the ~6.5% of rows without an issued_date still
     * carry a recency date. On a permit with none of those the release alias uses
     * `status_date`. `permit_date_source` records which column it came from
     * ('issued' | 'applied' | 'final' | 'status' | 'status_migration'; the last
     * is a system migration stamp, left out of recent counts).
     */
    permit_date: BqDate,
    permit_date_source: NullableString,
    /**
     * The source system's last-update date, served by the release alias.
     * `.default(null)`: the API selects the column only once the alias carries it.
     */
    status_date: BqDate.default(null),
    permit_type: NullableString,
    permit_subtype: NullableString,
    permit_class: NullableString,
    work_type: NullableString,
    /**
     * Served-layer derivations (molluskio#1401/#1403/#1404, landed on
     * `permits_blds_latest` via #1445). `permit_category` normalizes the noisy
     * per-source `permit_type` into a stable bucket; `permit_type_display` decodes
     * coded type strings for display; `is_residential` / `is_located` are cheap
     * flags (residential vs commercial; has usable coordinates).
     */
    permit_category: NullableString,
    permit_type_display: NullableString,
    is_residential: z.boolean().nullable(),
    is_located: z.boolean().nullable(),
    permit_scope: PermitScope.nullable(),
    permit_systems: StringArray,
    permit_features: StringArray,
    tags: StringArray,
    description: NullableString,
    /**
     * Auto-composed, `\n`-separated narrative of the descriptive columns plus
     * any publisher text without its own canonical column. Read this instead of
     * the internal raw `source_descriptive_text`. See `parseDescriptionFull`.
     *
     * `.default(null)` so a row missing the key entirely still parses: the column
     * is absent from the BQ table until molluskio writes rows under the
     * #17 schema, and the API omits it from the SELECT while absent. Once present
     * it is NULL on old rows and populated on freshly-loaded ones. Output type
     * stays `string | null`.
     */
    description_full: NullableString.default(null),
    estimated_cost: NullableNumber,
    total_fee: NullableNumber,
    /**
     * Modeled cost fallback (molluskio#1405, landed via #1445): when a permit has
     * no source-reported `estimated_cost` but has square footage, molluskio
     * imputes a value from the median $/sqft of comparable permits. Consumers who
     * want a value use COALESCE(estimated_cost, estimated_cost_imputed).
     * `is_cost_imputed` flags such rows; `cost_basis` explains the model (e.g.
     * "modeled: median $62.84/sqft … MODELED, not source-reported"). NULL/false
     * when no sqft or no index match — never invented. The column is NUMERIC in
     * BQ but the API CASTs it to FLOAT64, so it parses as a plain number here.
     */
    estimated_cost_imputed: NullableNumber,
    is_cost_imputed: z.boolean().nullable(),
    cost_basis: NullableString,
    status_normalized: PermitStatus.nullable(),
    contractor_name: NullableString,
    applicant_name: NullableString,
    owner_name: NullableString,
    parcel_apn: NullableString,
    error_class: NullableString,
    /**
     * Row class (molluskio#5385), deprecated in favour of the
     * `non_building_record` tag (molluskio#4504). `'non_building_excluded'`
     * marks a licence, inspection, enforcement or admin record: kept and counted
     * like every record, shown with a "Non-building record" badge. A row is
     * non-building when it has that tag OR this class — see `isNonBuilding`
     * (molluskio#6001).
     * `.default(null)` so a row without the key still parses.
     */
    record_class: NullableString.default(null),
});
export const PermitPlanningLink = z.object({
    link_id: z.string(),
    case_id: z.string(),
    source_case_number: NullableString,
    case_family: NullableString,
    title: NullableString,
    match_method: z.string(),
    match_confidence: z.number(),
    candidate_count: z.number().int(),
    is_ambiguous: z.boolean().nullable(),
    valid_on_case_window: z.boolean().nullable(),
    temporal_basis: NullableString,
});
export const PermitContractorLink = z.object({
    link_id: z.string(),
    entity_id: NullableString,
    credential_id: NullableString,
    authority_id: NullableString,
    match_method: z.string(),
    match_confidence: z.number(),
    candidate_count: z.number().int(),
    is_ambiguous: z.boolean().nullable(),
    valid_on_activity_date: z.boolean().nullable(),
    temporal_basis: NullableString,
});
export const PermitGraphResponse = z.object({
    permit: PermitRow,
    planning: z.array(PermitPlanningLink),
    contractors: z.array(PermitContractorLink),
});
//# sourceMappingURL=permit.js.map